/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): translate (debt) */
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { chronosRoute } from "../chronos";
import { AppErrorBoundary } from "@/components/ui/error-boundary";
import { isoToLocalDay } from "@/features/chronos-sync/timesheet";
import {
  Badge,
  Card,
  CardContent,
  GlassSurface,
  PageContainer,
  PageHeader,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { AlertCircle, BarChart3, Flame, Sparkles } from "@fleet/icons";
import {
  cn,
  liveApplicationLogFilters,
  liveProjectFilters,
  liveTimeEntryFilters,
  queryKeys,
} from "@time-tracker/shared";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import {
  and,
  application_log,
  type ApplicationLog,
  eq,
  project,
  type Project,
  sql,
  time_entry,
  type TimeEntry,
} from "@time-tracker/powersync";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { formatMinsStr, getCategoryForEntry, getColorClass } from "@/features/chronos-sync/helpers";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";

const HOURS_IN_DAY = 24;
const DAYS_IN_WEEK = 7;
const MINS_IN_HOUR = 60;
const SECS_IN_MIN = 60;
const MS_IN_SEC = 1000;
const MS_IN_MIN = 60_000;
const MS_IN_DAY = 86_400_000;
const FIVE_MINUTES_S = 300;
const DEFAULT_LOG_DURATION_S = 30;

const SUNDAY_INDEX = 6;
const SUNDAY_DIFF_DAYS = -6;

const SEVEN_DAYS = 7;
const THIRTY_DAYS = 30;
const TWENTY_NINE_DAYS = 29;
const SIXTY_DAYS = 60;

const DENSITY_LOW = 1;
const DENSITY_MEDIUM = 2;
const DENSITY_HIGH = 3;
const DENSITY_PEAK = 4;

const DENSITY_LOW_THRESHOLD_MINS = 15;
const DENSITY_MEDIUM_THRESHOLD_MINS = 30;
const DENSITY_HIGH_THRESHOLD_MINS = 45;

const SWITCHING_RECOVERY_MINS = 8;

const getDensityClass = (density: number) => {
  switch (density) {
    case DENSITY_LOW: {
      return "bg-[#afc6ff]/20 border border-[#afc6ff]/10";
    }
    case DENSITY_MEDIUM: {
      return "bg-[#afc6ff]/40 border border-[#afc6ff]/20";
    }
    case DENSITY_HIGH: {
      return "bg-[#afc6ff]/70 border border-[#afc6ff]/30";
    }
    case DENSITY_PEAK: {
      return "bg-[#afc6ff] border border-[#afc6ff]/50 shadow-[0_0_8px_rgba(175,198,255,0.4)]";
    }
    default: {
      return "bg-white/5 border border-white/5";
    }
  }
};

const SUNDAY_INDEX_MAPPED = 6;

function AnalyticsPage() {
  const { t } = useI18n();
  return (
    <AppErrorBoundary
      variant="region"
      label="analytics"
      title={t(msg`Analytics failed to load`)}
      description={t(msg`We could not compute your activity insights. Try again.`)}
    >
      <AnalyticsPageInner />
    </AppErrorBoundary>
  );
}

function AnalyticsPageInner() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { activeWorkspaceId } = useWorkspace();
  const { t } = useI18n();
  const navigate = useNavigate();

  // Deep-link support: the week/month range is shareable via ?range=. URL→signal
  // on load, signal→URL on toggle (replace), and an effect to mirror back/forward
  // + pasted links. Idempotent: reloading the URL reconstructs the same range.
  const search = useSearch({ from: chronosAnalyticsRoute.id });

  const [timeRange, setTimeRange] = createSignal<"week" | "month">(search().range ?? "week");

  const updateTimeRange = (value: "week" | "month") => {
    setTimeRange(value);
    void navigate({
      to: chronosAnalyticsRoute.to,
      // Preserve other search params (e.g. `?ws=`) and only touch `range`.
      search: (prev) => ({
        ...prev,
        range: value === "week" ? undefined : value,
      }),
      replace: true,
    });
  };

  // Comparison in `untrack`: only reacts to EXTERNAL changes to `?range=`; if
  // it tracks `timeRange()` (the signal that writes), a state-first change
  // re-triggers it with the old URL and reverts the range (bug #15b / CC-16).
  createEffect(() => {
    const urlRange = search().range ?? "week";
    if (untrack(() => urlRange !== timeRange())) {
      setTimeRange(urlRange);
    }
  });

  // Interconnection: clicking a bar deep-links to the timesheet, pre-filtered to
  // that single day so the user can drill into the entries behind the bar.
  const drillIntoDay = (dateKey: string) => {
    void navigate({
      to: "/chronos/timesheet",
      search: (prev) => ({
        ...prev,
        range: undefined,
        granularity: "day" as const,
        from: dateKey,
        to: dateKey,
      }),
    });
  };

  const hours = Array.from(
    { length: HOURS_IN_DAY },
    (_, i) => `${i.toString().padStart(2, "0")}:00`,
  );
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Query completed time entries for the last 60 days to support comparative metrics
  const timeEntries = createPowerSyncQuery<TimeEntry>({
    queryKey: () => queryKeys.chronos.analytics.timeEntries(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(time_entry)
        .where(
          and(
            eq(time_entry.user_id, userId()),
            sql`end_time IS NOT NULL`,
            sql`start_time >= ${new Date(Date.now() - SIXTY_DAYS * HOURS_IN_DAY * MINS_IN_HOUR * SECS_IN_MIN * MS_IN_SEC).toISOString()}`,
            ...liveTimeEntryFilters(),
          ),
        );
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Query raw application logs for context switching metrics
  const appLogs = createPowerSyncQuery<ApplicationLog>({
    queryKey: () => queryKeys.chronos.analytics.rawAppLogs(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(application_log)
        .where(and(eq(application_log.user_id, userId()), ...liveApplicationLogFilters()));
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Query all projects for category mapping
  const projects = createPowerSyncQuery<Project>({
    queryKey: () => queryKeys.chronos.analytics.projects(userId(), activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(project)
        .where(and(eq(project.workspace_id, activeWorkspaceId()), ...liveProjectFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  const todayIdx = () => {
    const d = new Date().getDay();
    return d === 0 ? SUNDAY_INDEX : d - 1;
  };

  const currentRangeHours = createMemo(() => {
    const entries = timeEntries.data ?? [];
    let totalMins = 0;
    const now = Date.now();
    const rangeMs =
      (timeRange() === "week" ? SEVEN_DAYS : THIRTY_DAYS) *
      HOURS_IN_DAY *
      MINS_IN_HOUR *
      SECS_IN_MIN *
      MS_IN_SEC;
    const cutoff = now - rangeMs;

    for (const entry of entries) {
      if (!entry.start_time || !entry.end_time) {
        continue;
      }
      try {
        const start = new Date(entry.start_time).getTime();
        const end = new Date(entry.end_time).getTime();
        if (start >= cutoff && start <= now) {
          totalMins += Math.max(0, Math.floor((end - start) / MS_IN_MIN));
        }
      } catch (err) {
        console.error(err);
      }
    }
    return Number.parseFloat((totalMins / MINS_IN_HOUR).toFixed(1));
  });

  const previousRangeHours = createMemo(() => {
    const entries = timeEntries.data ?? [];
    let totalMins = 0;
    const now = Date.now();
    const rangeMs =
      (timeRange() === "week" ? SEVEN_DAYS : THIRTY_DAYS) *
      HOURS_IN_DAY *
      MINS_IN_HOUR *
      SECS_IN_MIN *
      MS_IN_SEC;
    const startOfPrevRange = now - 2 * rangeMs;
    const endOfPrevRange = now - rangeMs;

    for (const entry of entries) {
      if (!entry.start_time || !entry.end_time) {
        continue;
      }
      try {
        const start = new Date(entry.start_time).getTime();
        const end = new Date(entry.end_time).getTime();
        if (start >= startOfPrevRange && start < endOfPrevRange) {
          totalMins += Math.max(0, Math.floor((end - start) / MS_IN_MIN));
        }
      } catch (err) {
        console.error(err);
      }
    }
    return Number.parseFloat((totalMins / MINS_IN_HOUR).toFixed(1));
  });

  const chartData = createMemo(() => {
    const entries = timeEntries.data ?? [];
    if (timeRange() === "week") {
      const totals = Array.from({ length: DAYS_IN_WEEK }, () => 0); // Monday to Sunday
      const now = new Date();
      const currentDay = now.getDay();
      const diffToMonday = currentDay === 0 ? SUNDAY_DIFF_DAYS : 1 - currentDay;
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() + diffToMonday);
      startOfWeek.setHours(0, 0, 0, 0);
      const startOfWeekMs = startOfWeek.getTime();
      const endOfWeekMs =
        startOfWeekMs + DAYS_IN_WEEK * HOURS_IN_DAY * MINS_IN_HOUR * SECS_IN_MIN * MS_IN_SEC;

      for (const entry of entries) {
        if (!entry.start_time || !entry.end_time) {
          continue;
        }
        const parseTimes = () => {
          try {
            return {
              start: new Date(entry.start_time ?? "").getTime(),
              end: new Date(entry.end_time ?? "").getTime(),
              valid: true,
            };
          } catch (err) {
            console.error(err);
            return { start: 0, end: 0, valid: false };
          }
        };

        const { start, end, valid } = parseTimes();
        if (!valid) {
          continue;
        }

        if (start < startOfWeekMs || start >= endOfWeekMs) {
          continue;
        }

        const durationMins = Math.max(0, Math.floor((end - start) / MS_IN_MIN));
        const entryDate = new Date(entry.start_time);
        const jsDay = entryDate.getDay();
        const idx = jsDay === 0 ? SUNDAY_INDEX : jsDay - 1;
        const currentVal = totals[idx];
        if (currentVal !== undefined) {
          totals[idx] = currentVal + durationMins;
        }
      }

      return totals.map((mins, idx) => {
        const dayDate = new Date(startOfWeekMs);
        dayDate.setDate(dayDate.getDate() + idx);
        return {
          label: ["L", "M", "X", "J", "V", "S", "D"][idx] ?? "",
          fullName:
            ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"][idx] ?? "",
          hours: Number.parseFloat((mins / MINS_IN_HOUR).toFixed(1)),
          isToday: idx === todayIdx(),
          dateKey: isoToLocalDay(dayDate.toISOString()),
        };
      });
    }
    // Month (last 30 days)
    const now = new Date();
    const totals = Array.from({ length: THIRTY_DAYS }, () => 0);
    const dates = Array.from({ length: THIRTY_DAYS }, (_, i) => {
      const d = new Date(now);
      d.setDate(now.getDate() - (TWENTY_NINE_DAYS - i));
      d.setHours(0, 0, 0, 0);
      return d;
    });

    for (const entry of entries) {
      if (!entry.start_time || !entry.end_time) {
        continue;
      }
      try {
        const start = new Date(entry.start_time).getTime();
        const end = new Date(entry.end_time).getTime();

        const entryDate = new Date(entry.start_time);
        entryDate.setHours(0, 0, 0, 0);
        const entryDateMs = entryDate.getTime();

        const dayIdx = dates.findIndex((d) => d.getTime() === entryDateMs);
        if (dayIdx !== -1) {
          const durationMins = Math.max(0, Math.floor((end - start) / MS_IN_MIN));
          const currentVal = totals[dayIdx];
          if (currentVal !== undefined) {
            totals[dayIdx] = currentVal + durationMins;
          }
        }
      } catch (err) {
        console.error(err);
      }
    }

    return dates.map((date, idx) => {
      const mins = totals[idx] ?? 0;
      const isToday = date.getDate() === now.getDate() && date.getMonth() === now.getMonth();
      const label = date.getDate().toString();
      const fullName = date.toLocaleDateString([], { month: "long", day: "numeric" });
      return {
        label,
        fullName,
        hours: Number.parseFloat((mins / MINS_IN_HOUR).toFixed(1)),
        isToday,
        dateKey: isoToLocalDay(date.toISOString()),
      };
    });
  });

  const dailyAchievement = createMemo(() => {
    const totals = chartData().map((d) => d.hours);
    const now = new Date();
    const getTodayHrs = () => {
      if (timeRange() === "week") {
        return totals[todayIdx()] ?? 0;
      }
      const todayDateStr = now.getDate().toString();
      const todayItem = chartData().find((d) => d.label === todayDateStr && d.isToday);
      return todayItem ? todayItem.hours : 0;
    };
    const todayHrs = getTodayHrs();

    const sumHrs = totals.reduce((a, b) => a + b, 0);
    const avgHrs = sumHrs / totals.length;

    if (avgHrs === 0) {
      return {
        ratio: 0,
        text: t(msg`No activity logged in this period yet.`),
        isPositive: true,
      };
    }

    const pctChange = Math.round(((todayHrs - avgHrs) / avgHrs) * 100);
    const isPositive = pctChange >= 0;

    const pct = Math.abs(pctChange);
    const period = timeRange() === "week" ? t(msg`week`) : t(msg`month`);
    const getText = () => {
      if (pctChange === 0) {
        return t(msg`You are right on track with your period average.`);
      }
      return isPositive
        ? t(
            msg`You have been ${pct}% more productive today compared to your average for this ${period}.`,
          )
        : t(
            msg`You have been ${pct}% less productive today compared to your average for this ${period}.`,
          );
    };
    const text = getText();

    return {
      ratio: pctChange,
      text,
      isPositive,
    };
  });

  const categoryDistribution = createMemo(() => {
    const entries = timeEntries.data ?? [];
    const projList = projects.data ?? [];

    const projMap = new Map<string, string>();
    for (const p of projList) {
      projMap.set(p.id, p.code ?? "");
    }

    const now = new Date();
    const currentDay = now.getDay();
    const diffToMonday = currentDay === 0 ? SUNDAY_DIFF_DAYS : 1 - currentDay;
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() + diffToMonday);
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfWeekMs = startOfWeek.getTime();

    let codingMins = 0;
    let designMins = 0;
    let adminMins = 0;

    for (const entry of entries) {
      if (!entry.start_time || !entry.end_time) {
        continue;
      }
      try {
        const start = new Date(entry.start_time).getTime();
        const end = new Date(entry.end_time).getTime();

        if (timeRange() === "week" && start < startOfWeekMs) {
          continue;
        }

        const durationMins = Math.max(0, Math.floor((end - start) / MS_IN_MIN));

        const projCode = entry.project_id ? (projMap.get(entry.project_id) ?? "") : "";
        const desc = entry.description ?? "";

        const category = getCategoryForEntry(desc, projCode);
        if (category === "design") {
          designMins += durationMins;
        } else if (category === "admin") {
          adminMins += durationMins;
        } else {
          codingMins += durationMins;
        }
      } catch (err) {
        console.error(err);
      }
    }

    const totalMins = codingMins + designMins + adminMins;

    if (totalMins === 0) {
      return {
        totalStr: "0m",
        categories: [
          {
            name: t(msg`Coding`),
            minsStr: "0m",
            pct: 0,
            color: "bg-primary",
            text: "text-primary",
          },
          {
            name: t(msg`Design`),
            minsStr: "0m",
            pct: 0,
            color: "bg-tertiary",
            text: "text-[#ebb2ff]",
          },
          {
            name: t(msg`Admin`),
            minsStr: "0m",
            pct: 0,
            color: "bg-emerald-500",
            text: "text-emerald-400",
          },
        ],
      };
    }

    return {
      totalStr: formatMinsStr(totalMins),
      categories: [
        {
          name: t(msg`Coding`),
          minsStr: formatMinsStr(codingMins),
          pct: Math.round((codingMins / totalMins) * 100),
          color: "bg-primary",
          text: "text-primary",
        },
        {
          name: t(msg`Design`),
          minsStr: formatMinsStr(designMins),
          pct: Math.round((designMins / totalMins) * 100),
          color: "bg-tertiary",
          text: "text-[#ebb2ff]",
        },
        {
          name: t(msg`Admin`),
          minsStr: formatMinsStr(adminMins),
          pct: Math.round((adminMins / totalMins) * 100),
          color: "bg-emerald-500",
          text: "text-emerald-400",
        },
      ],
    };
  });

  // Per-project time breakdown for the active range. Powers the horizontal bar
  // report; each bar deep-links to the timesheet filtered to that project's
  // recent window so the user can drill from chart → underlying entries.
  const projectBreakdown = createMemo(() => {
    const entries = timeEntries.data ?? [];
    const projList = projects.data ?? [];

    const nameById = new Map<string, { name: string; color: string }>();
    for (const p of projList) {
      nameById.set(p.id, { name: p.name ?? p.code ?? "—", color: p.color ?? "bg-blue-500" });
    }

    const now = Date.now();
    const rangeMs =
      (timeRange() === "week" ? SEVEN_DAYS : THIRTY_DAYS) *
      HOURS_IN_DAY *
      MINS_IN_HOUR *
      SECS_IN_MIN *
      MS_IN_SEC;
    const cutoff = now - rangeMs;

    const minsByProject = new Map<string, number>();
    for (const entry of entries) {
      if (!entry.start_time || !entry.end_time) {
        continue;
      }
      const start = new Date(entry.start_time).getTime();
      const end = new Date(entry.end_time).getTime();
      if (start < cutoff || start > now || end <= start) {
        continue;
      }
      const key = entry.project_id ?? "";
      minsByProject.set(key, (minsByProject.get(key) ?? 0) + Math.floor((end - start) / MS_IN_MIN));
    }

    const rows = [...minsByProject.entries()]
      .map(([id, mins]) => {
        const meta = nameById.get(id);
        return {
          id,
          name: meta?.name ?? t(msg`No Project`),
          color: getColorClass(meta?.color ?? "bg-blue-500"),
          mins,
        };
      })
      .toSorted((a, b) => b.mins - a.mins);

    const max = Math.max(...rows.map((r) => r.mins), 1);
    return rows.map((r) => ({ ...r, pct: Math.round((r.mins / max) * 100) }));
  });

  const drillIntoRange = () => {
    const now = new Date();
    const rangeDays = timeRange() === "week" ? SEVEN_DAYS : THIRTY_DAYS;
    const from = isoToLocalDay(new Date(now.getTime() - rangeDays * MS_IN_DAY).toISOString());
    void navigate({
      to: "/chronos/timesheet",
      search: (prev) => ({
        ...prev,
        range: undefined,
        granularity: "day" as const,
        from,
        to: isoToLocalDay(now.toISOString()),
      }),
    });
  };

  // Generate density grid values based on real time entries
  const heatmapData = createMemo(() => {
    const entries = timeEntries.data ?? [];
    const grid = Array.from({ length: DAYS_IN_WEEK }, () =>
      Array.from({ length: HOURS_IN_DAY }, () => 0),
    );

    const now = new Date();
    const currentDay = now.getDay();
    const diffToMonday = currentDay === 0 ? SUNDAY_DIFF_DAYS : 1 - currentDay;
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() + diffToMonday);
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfWeekMs = startOfWeek.getTime();

    for (const entry of entries) {
      if (!entry.start_time || !entry.end_time) {
        continue;
      }

      try {
        const start = new Date(entry.start_time);
        const end = new Date(entry.end_time);

        if (timeRange() === "week" && start.getTime() < startOfWeekMs) {
          continue;
        }

        const durationMins = Math.max(
          0,
          Math.floor((end.getTime() - start.getTime()) / (SECS_IN_MIN * MS_IN_SEC)),
        );

        const jsDay = start.getDay();
        // Map: Sunday = 6, Monday = 0, Tuesday = 1, etc.
        const dayIdx = jsDay === 0 ? SUNDAY_INDEX_MAPPED : jsDay - 1;
        const hourIdx = start.getHours();

        const row = grid[dayIdx];
        if (row && hourIdx >= 0 && hourIdx < HOURS_IN_DAY) {
          const currentVal = row[hourIdx] ?? 0;
          row[hourIdx] = currentVal + durationMins;
        }
      } catch (err) {
        console.error("Failed to parse time entry timestamps in analytics", err);
      }
    }

    const isMonth = timeRange() === "month";
    const factor = isMonth ? 4 : 1;

    const thresholdLow = DENSITY_LOW_THRESHOLD_MINS * factor;
    const thresholdMedium = DENSITY_MEDIUM_THRESHOLD_MINS * factor;
    const thresholdHigh = DENSITY_HIGH_THRESHOLD_MINS * factor;

    // Map accumulated minutes to density ranks
    return grid.map((row) =>
      row.map((mins) => {
        if (mins === 0) {
          return 0;
        }
        if (mins <= thresholdLow) {
          return DENSITY_LOW;
        }
        if (mins <= thresholdMedium) {
          return DENSITY_MEDIUM;
        }
        if (mins <= thresholdHigh) {
          return DENSITY_HIGH;
        }
        return DENSITY_PEAK;
      }),
    );
  });

  // Calculate peak performance day/hour dynamically
  const peakTime = createMemo(() => {
    const grid = heatmapData();
    let maxDensity = -1;
    let peakDayIdx = 1; // Tue default
    let peakHour = 9; // 09:00 default

    for (let d = 0; d < DAYS_IN_WEEK; d += 1) {
      const row = grid[d];
      if (row) {
        for (let h = 0; h < HOURS_IN_DAY; h += 1) {
          const val = row[h] ?? 0;
          if (val > maxDensity) {
            maxDensity = val;
            peakDayIdx = d;
            peakHour = h;
          }
        }
      }
    }

    return {
      day: days[peakDayIdx],
      hour: peakHour,
      hasData: maxDensity > 0,
    };
  });

  // Group application logs into blocks of time for context switching analysis
  const groupedActivityBlocks = createMemo<{ appCount: number }[]>(() => {
    const logs = appLogs.data ?? [];
    if (logs.length === 0) {
      return [];
    }

    const sortedLogs = logs.toSorted(
      (a, b) => new Date(a.start_time ?? "").getTime() - new Date(b.start_time ?? "").getTime(),
    );

    const groups: { appCount: number }[] = [];
    let currentGroup: {
      startTime: string;
      endTime: string;
      appNames: Set<string>;
    } | null = null;

    for (const log of sortedLogs) {
      const logStart = new Date(log.start_time ?? "").getTime();
      const logDurationMs = (log.duration_seconds ?? DEFAULT_LOG_DURATION_S) * MS_IN_SEC;
      const logEnd = logStart + logDurationMs;

      if (currentGroup) {
        const lastGroupEnd = new Date(currentGroup.endTime).getTime();
        const gapSeconds = (logStart - lastGroupEnd) / MS_IN_SEC;

        if (gapSeconds <= FIVE_MINUTES_S) {
          currentGroup.endTime = new Date(Math.max(lastGroupEnd, logEnd)).toISOString();
          currentGroup.appNames.add(log.application_name ?? "");
        } else {
          groups.push({
            appCount: currentGroup.appNames.size,
          });
          currentGroup = {
            startTime: log.start_time ?? "",
            endTime: new Date(logEnd).toISOString(),
            appNames: new Set([log.application_name ?? ""]),
          };
        }
      } else {
        currentGroup = {
          startTime: log.start_time ?? "",
          endTime: new Date(logEnd).toISOString(),
          appNames: new Set([log.application_name ?? ""]),
        };
      }
    }

    if (currentGroup) {
      groups.push({
        appCount: currentGroup.appNames.size,
      });
    }

    return groups;
  });

  // Process application logs to count context switches
  const switchingMetrics = createMemo(() => {
    const blocks = groupedActivityBlocks();
    let multiAppCount = 0;

    for (const b of blocks) {
      if (b.appCount > 1) {
        multiAppCount += 1;
      }
    }

    const costMins = multiAppCount * SWITCHING_RECOVERY_MINS;

    return {
      multiAppCount,
      costMins,
    };
  });

  const switchingInsightText = () => {
    const blocks = switchingMetrics().multiAppCount;
    const mins = switchingMetrics().costMins;
    return t(
      msg`Multi-app task switching was detected in ${blocks} active work blocks. This fragmentation cost you approximately ${mins} minutes of cognitive recovery time.`,
    );
  };

  const peakInsightText = () => {
    const day = peakTime().day ?? "";
    const hour = peakTime().hour.toString().padStart(2, "0");
    return t(
      msg`Your tracking density is highest on ${day}s around ${hour}:00. Block this window from meetings to protect deep focus.`,
    );
  };

  const insights = createMemo(() => [
    {
      title: t(msg`Context-Switching Cost`),
      description:
        switchingMetrics().multiAppCount > 0
          ? switchingInsightText()
          : t(
              msg`No task switching patterns detected. Your current work logs show high focus cohesion.`,
            ),
      icon: AlertCircle,
      iconColor: "text-amber-400",
      bgClass: "border-amber-500/20 bg-amber-500/5",
    },
    {
      title: t(msg`Peak Performance Zone`),
      description: peakTime().hasData
        ? peakInsightText()
        : t(
            msg`Not enough tracking history to determine peak performance zone. Start logging focus to generate zones.`,
          ),
      icon: Flame,
      iconColor: "text-[#ebb2ff]",
      bgClass: "border-[#ebb2ff]/20 bg-[#ebb2ff]/5",
    },
  ]);

  return (
    <PageContainer
      maxWidth="7xl"
      class="space-y-8"
      containerClass="bg-[#000000] text-white pb-[calc(5.5rem+var(--safe-area-inset-bottom))]"
    >
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-white/5 pb-4">
        <PageHeader
          title={t(msg`Chronos Analytics`)}
          description={t(msg`Workspace activity patterns and load measurements.`)}
          icon={BarChart3}
        />
        {/* Selector de Rango (Semana / Mes) */}
        <div class="flex bg-white/[0.03] border border-white/10 p-1 rounded-full shrink-0 h-10 w-fit items-center font-mono">
          <button
            type="button"
            onClick={() => updateTimeRange("week")}
            class={cn(
              "px-4 py-1.5 text-xs font-bold rounded-full cursor-pointer transition-[color,background-color,box-shadow] duration-200 will-change-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#afc6ff]/50",
              timeRange() === "week"
                ? "bg-[#afc6ff] text-black shadow-[0_2px_8px_rgba(175,198,255,0.3)]"
                : "text-muted-foreground hover:text-white",
            )}
          >
            {t(msg`Week`)}
          </button>
          <button
            type="button"
            onClick={() => updateTimeRange("month")}
            class={cn(
              "px-4 py-1.5 text-xs font-bold rounded-full cursor-pointer transition-[color,background-color,box-shadow] duration-200 will-change-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#afc6ff]/50",
              timeRange() === "month"
                ? "bg-[#afc6ff] text-black shadow-[0_2px_8px_rgba(175,198,255,0.3)]"
                : "text-muted-foreground hover:text-white",
            )}
          >
            {t(msg`Month`)}
          </button>
        </div>
      </div>

      {/* Bento Grid: Chart & Comparison Cards */}
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Column 1 & 2: Interactive Weekly/Monthly Bar Chart */}
        <GlassSurface class="lg:col-span-2 p-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none flex flex-col justify-between">
          <div>
            <h3 class="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
              {timeRange() === "week" ? "Weekly Activity Chart" : "Monthly Activity Chart"}
            </h3>
            <p class="text-xs text-muted-foreground">
              {timeRange() === "week"
                ? "Tracked hours compared to daily average for this week"
                : "Tracked hours for the last 30 days"}
            </p>
          </div>

          {/* Vertical Bar Chart */}
          <div class="h-64 flex items-end justify-between gap-1 sm:gap-2 md:gap-4 px-2 pt-6 overflow-x-auto">
            <For each={chartData()}>
              {(item) => {
                const hrs = () => item.hours;
                const maxHrs = () => Math.max(...chartData().map((d) => d.hours), 1);
                const heightPct = () => (hrs() / maxHrs()) * 100;
                const barLabel = () => {
                  const dayName = item.fullName;
                  const hoursValue = hrs();
                  return t(msg`View entries for ${dayName}: ${hoursValue} hours`);
                };

                return (
                  <div class="flex flex-col items-center flex-1 min-w-[12px] group">
                    <Tooltip>
                      <TooltipTrigger
                        type="button"
                        onClick={() => drillIntoDay(item.dateKey)}
                        aria-label={barLabel()}
                        class="w-full flex justify-center cursor-pointer rounded-t-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#afc6ff]/60"
                      >
                        <div class="w-full max-w-[40px] bg-white/[0.03] border border-white/5 rounded-t-lg h-44 flex items-end overflow-hidden hover:border-primary/20 transition-colors duration-300">
                          <div
                            class={cn(
                              "w-full rounded-t-md transition-[height,background-color] duration-500",
                              item.isToday
                                ? "bg-gradient-to-t from-primary to-[#ebb2ff] shadow-[0_0_12px_rgba(235,178,255,0.4)]"
                                : "bg-white/15 group-hover:bg-white/20",
                            )}
                            style={{ height: `${heightPct()}%` }}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent class="bg-black/95 border border-white/10 text-white px-2.5 py-1.5 rounded-lg text-xs font-mono">
                        <span class="font-bold text-[#ebb2ff]">{item.fullName}</span>: {hrs()}h
                        <span class="block text-[10px] text-white/50">
                          {t(msg`Tap to view entries`)}
                        </span>
                      </TooltipContent>
                    </Tooltip>
                    <span
                      class={cn(
                        "text-[9px] sm:text-xs font-bold font-mono mt-3 transition-colors truncate w-full text-center",
                        item.isToday
                          ? "text-primary"
                          : "text-muted-foreground group-hover:text-white",
                      )}
                    >
                      {item.label}
                    </span>
                  </div>
                );
              }}
            </For>
          </div>
        </GlassSurface>

        {/* Column 3: Comparison Bento Widgets */}
        <div class="flex flex-col gap-6">
          {/* Daily Achievement Card */}
          <GlassSurface class="p-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none flex flex-col justify-between flex-1">
            <div class="space-y-2">
              <span class="text-[10px] font-bold text-[#ebb2ff] uppercase tracking-widest font-mono">
                {t(msg`Daily Goal`)}
              </span>
              <h3 class="text-lg font-extrabold text-white">{t(msg`Daily Achievement`)}</h3>
              <p class="text-xs text-muted-foreground leading-relaxed mt-2">
                {dailyAchievement().text}
              </p>
            </div>
            <div class="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
              <span class="text-xs text-muted-foreground font-mono">{t(msg`Trend`)}</span>
              <Badge
                class={cn(
                  "font-mono text-xs font-bold",
                  dailyAchievement().isPositive
                    ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                    : "bg-red-500/15 text-red-400 border border-red-500/20",
                )}
              >
                {dailyAchievement().ratio >= 0 ? "+" : ""}
                {dailyAchievement().ratio}%
              </Badge>
            </div>
          </GlassSurface>

          {/* Trend Comparison Card (MoM / WoW) */}
          <GlassSurface class="p-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none flex flex-col justify-between flex-1">
            <div class="space-y-2">
              <span class="text-[10px] font-bold text-[#ebb2ff] uppercase tracking-widest font-mono">
                {timeRange() === "week" ? t(msg`Weekly Trend`) : t(msg`Monthly Trend`)}
              </span>
              <h3 class="text-lg font-extrabold text-white font-mono">
                {timeRange() === "week" ? t(msg`WoW Progress`) : t(msg`MoM Progress`)}
              </h3>
              <div class="flex items-baseline gap-2 mt-2">
                <span class="text-2xl font-black text-white">{currentRangeHours()}h</span>
                <span class="text-xs text-muted-foreground font-mono">
                  {t(msg`vs {hours}h last {period}`, {
                    hours: previousRangeHours(),
                    period: timeRange() === "week" ? t(msg`week`) : t(msg`month`),
                  })}
                </span>
              </div>
            </div>

            <div class="mt-4 space-y-2 pt-4 border-t border-white/5">
              <div class="flex justify-between text-[10px] font-bold text-muted-foreground font-mono">
                <span>{t(msg`Comparison Track`)}</span>
                <span
                  class={cn(
                    "font-mono",
                    currentRangeHours() >= previousRangeHours()
                      ? "text-emerald-400"
                      : "text-amber-400",
                  )}
                >
                  {currentRangeHours() >= previousRangeHours() ? t(msg`Upward`) : t(msg`Downward`)}
                </span>
              </div>
              <div class="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                <div
                  class={cn(
                    "h-full rounded-full transition-[width,background-color] duration-500",
                    currentRangeHours() >= previousRangeHours() ? "bg-emerald-500" : "bg-amber-500",
                  )}
                  style={{
                    width: `${Math.min(100, previousRangeHours() > 0 ? (currentRangeHours() / previousRangeHours()) * 100 : 100)}%`,
                  }}
                />
              </div>
            </div>
          </GlassSurface>
        </div>
      </div>

      {/* Productivity Heatmap Grid */}
      <GlassSurface class="p-6 space-y-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none">
        <div>
          <h3 class="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
            {t(msg`Productivity Density Grid`)}
          </h3>
          <p class="text-xs text-muted-foreground">
            {timeRange() === "week"
              ? "Hourly activity breakdown mapped over the past week"
              : "Hourly activity breakdown aggregated over the past 30 days"}
          </p>
        </div>

        <div class="overflow-x-auto select-none pt-2 [scrollbar-width:thin]">
          <div class="min-w-[560px] space-y-2 sm:min-w-[760px]">
            {/* Hours Labels */}
            <div class="flex items-center gap-1 pl-8 text-[9px] font-bold text-muted-foreground uppercase tracking-wider font-mono sm:gap-1.5 sm:pl-10">
              <For each={hours}>
                {(hour) => <div class="w-5 text-center shrink-0 sm:w-7">{hour.split(":")[0]}</div>}
              </For>
            </div>

            {/* Grid rows */}
            <For each={days}>
              {(day, dayIdx) => (
                <div class="flex items-center gap-1 sm:gap-1.5">
                  <div class="w-8 text-[10px] font-bold text-muted-foreground font-mono sm:w-10">
                    {day}
                  </div>
                  <div class="flex items-center gap-1 sm:gap-1.5">
                    <For each={heatmapData()[dayIdx()]}>
                      {(density) => (
                        <div
                          class={cn(
                            "w-5 h-5 rounded-md transition-[transform,background-color] duration-300 will-change-transform hover:scale-110 sm:w-7 sm:h-7",
                            getDensityClass(density),
                          )}
                        />
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>

        {/* Legend */}
        <div class="flex items-center justify-end gap-2 text-[9px] font-bold text-muted-foreground uppercase tracking-wider font-mono">
          <span>{t(msg`Less active`)}</span>
          <div class="w-3.5 h-3.5 rounded-sm bg-white/5" />
          <div class="w-3.5 h-3.5 rounded-sm bg-[#afc6ff]/20" />
          <div class="w-3.5 h-3.5 rounded-sm bg-[#afc6ff]/40" />
          <div class="w-3.5 h-3.5 rounded-sm bg-[#afc6ff]/70" />
          <div class="w-3.5 h-3.5 rounded-sm bg-[#afc6ff]" />
          <span>{t(msg`More active`)}</span>
        </div>
      </GlassSurface>

      {/* Time Distribution Widget */}
      <GlassSurface class="p-6 space-y-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none">
        <div>
          <h3 class="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
            Time Distribution
          </h3>
          <p class="text-xs text-muted-foreground">
            {timeRange() === "week" ? "Weekly" : "Monthly"} breakdown across core operational
            categories ({categoryDistribution().totalStr} total tracked)
          </p>
        </div>

        {/* Multi-segmented progress bar */}
        <div class="space-y-4">
          <div class="flex h-4 w-full rounded-full overflow-hidden bg-white/5 border border-white/10">
            <For each={categoryDistribution().categories}>
              {(cat) => (
                <Show when={cat.pct > 0}>
                  <div
                    class={cat.color}
                    style={{ width: `${cat.pct}%` }}
                    title={`${cat.name}: ${cat.pct}% (${cat.minsStr})`}
                  />
                </Show>
              )}
            </For>
          </div>

          {/* Legends */}
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <For each={categoryDistribution().categories}>
              {(cat) => (
                <div class="flex items-center justify-between p-3 rounded-lg bg-white/[0.01] border border-white/5">
                  <div class="flex items-center gap-2">
                    <span class={`w-2.5 h-2.5 rounded-full ${cat.color}`} />
                    <span class="text-xs font-bold text-foreground font-mono">{cat.name}</span>
                  </div>
                  <div class="text-right">
                    <span class={`text-xs font-bold font-mono ${cat.text}`}>{cat.pct}%</span>
                    <span class="text-[10px] text-muted-foreground font-mono block">
                      {cat.minsStr}
                    </span>
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>
      </GlassSurface>

      {/* Per-project breakdown — horizontal bar report, drillable to timesheet */}
      <GlassSurface class="p-6 space-y-5 bg-[#121212] border border-white/10 rounded-[24px] shadow-none">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
              {t(msg`Time by Project`)}
            </h3>
            <p class="text-xs text-muted-foreground">
              {timeRange() === "week"
                ? t(msg`Tracked hours per project over the past week`)
                : t(msg`Tracked hours per project over the past 30 days`)}
            </p>
          </div>
          <Show when={projectBreakdown().length > 0}>
            <button
              type="button"
              onClick={drillIntoRange}
              class="shrink-0 font-mono text-[11px] text-[#afc6ff] hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#afc6ff]/60 rounded px-1"
            >
              {t(msg`Open timesheet`)} →
            </button>
          </Show>
        </div>

        <Show
          when={projectBreakdown().length > 0}
          fallback={
            <p class="text-xs text-muted-foreground font-mono py-4 text-center">
              {t(msg`No project time logged in this period yet.`)}
            </p>
          }
        >
          <div class="space-y-3">
            <For each={projectBreakdown()}>
              {(row) => {
                const rowName = row.name;
                const rowDuration = formatMinsStr(row.mins);
                return (
                  <button
                    type="button"
                    onClick={drillIntoRange}
                    aria-label={t(msg`View timesheet — ${rowName}: ${rowDuration}`)}
                    class="block w-full text-left group cursor-pointer rounded-lg p-1 -m-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#afc6ff]/40 transition-colors hover:bg-white/[0.02]"
                  >
                    <div class="flex items-center justify-between mb-1.5">
                      <div class="flex items-center gap-2 min-w-0">
                        <span class={cn("w-2.5 h-2.5 rounded-full shrink-0", row.color.bar)} />
                        <span class="text-xs font-bold text-foreground font-mono truncate">
                          {row.name}
                        </span>
                      </div>
                      <span class={cn("text-xs font-bold font-mono shrink-0 ml-2", row.color.text)}>
                        {formatMinsStr(row.mins)}
                      </span>
                    </div>
                    <div class="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                      <div
                        class={cn(
                          "h-full rounded-full transition-[width,opacity] duration-500 group-hover:opacity-90",
                          row.color.bar,
                        )}
                        style={{ width: `${row.pct}%` }}
                      />
                    </div>
                  </button>
                );
              }}
            </For>
          </div>
        </Show>
      </GlassSurface>

      {/* Insights Section (heurísticas locales sobre los logs de actividad) */}
      <div class="space-y-4">
        <div class="flex items-center gap-2">
          <Sparkles class="h-4.5 w-4.5 text-[#afc6ff]" />
          <h3 class="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
            {t(msg`Insights & Optimization`)}
          </h3>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <For each={insights()}>
            {(insight) => (
              <Card
                class={cn("border border-white/10 bg-[#121212] overflow-hidden", insight.bgClass)}
              >
                <CardContent class="p-5 space-y-4">
                  <div class="flex items-center gap-2">
                    <insight.icon class={cn("h-5 w-5 shrink-0", insight.iconColor)} />
                    <h4 class="text-sm font-bold text-foreground font-mono">{insight.title}</h4>
                  </div>
                  <p class="text-xs text-muted-foreground/90 leading-relaxed font-medium">
                    {insight.description}
                  </p>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </div>
    </PageContainer>
  );
}

interface AnalyticsSearch {
  range?: "week" | "month";
}

export const chronosAnalyticsRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/analytics",
  validateSearch: (raw: Record<string, unknown>): AnalyticsSearch => ({
    range: raw.range === "month" ? "month" : undefined,
  }),
  component: AnalyticsPage,
});
